import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  PlayArrow as RunIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  FileCopyOutlined as DuplicateIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  PersonOutline as OwnerIcon,
  EventOutlined as ModifiedIcon,
  History as LastRunIcon,
} from '@mui/icons-material';
import { PIPELINE_STATUS } from '../../utils/constants';

const statusColors = {
  [PIPELINE_STATUS.DRAFT]: 'default',
  [PIPELINE_STATUS.ACTIVE]: 'success',
  [PIPELINE_STATUS.PAUSED]: 'warning',
  [PIPELINE_STATUS.ARCHIVED]: 'error',
};

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};

const clamp = (lines) => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lines,
  overflow: 'hidden',
  wordBreak: 'break-word',
});

const MetaRow = ({ icon: Icon, label, value, title }) => (
  <Box
    sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
    title={title || undefined}
  >
    <Icon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} aria-hidden />
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, width: 84 }}>
      {label}
    </Typography>
    <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
      {value}
    </Typography>
  </Box>
);

const PipelineCard = ({ pipeline, onOpen, onDuplicate, onRun, onMenuOpen }) => {
  const isPublic = pipeline.visibility === 'public';
  const canRun = pipeline.status === PIPELINE_STATUS.ACTIVE;
  const modifiedRaw = pipeline.updated_at || pipeline.created_at;
  const description = pipeline.description?.trim();

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:hover': { borderColor: 'primary.light', boxShadow: 2 },
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5, pb: 2 }}>
        {/* Header: title + options */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Tooltip title={pipeline.name} enterDelay={600} placement="top-start">
            <Typography
              variant="h6"
              component="h2"
              className="pipeline-card-title"
              sx={{ flexGrow: 1, minWidth: 0, lineHeight: 1.35, ...clamp(2) }}
            >
              {pipeline.name}
            </Typography>
          </Tooltip>
          <IconButton
            size="small"
            onClick={(e) => onMenuOpen(e, pipeline)}
            aria-label="pipeline options"
            sx={{ mt: -0.5, mr: -1, flexShrink: 0 }}
          >
            <MoreIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Chips */}
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip
            label={pipeline.status ? pipeline.status.charAt(0).toUpperCase() + pipeline.status.slice(1) : 'Unknown'}
            size="small"
            color={statusColors[pipeline.status] || 'default'}
            variant={pipeline.status === PIPELINE_STATUS.DRAFT ? 'outlined' : 'filled'}
          />
          <Chip
            icon={isPublic ? <PublicIcon /> : <LockIcon />}
            label={isPublic ? 'Public' : 'Private'}
            size="small"
            color={isPublic ? 'primary' : 'default'}
            variant="outlined"
          />
        </Stack>

        {/* Description: 3 lines, full text on hover */}
        <Tooltip
          title={description || ''}
          enterDelay={500}
          placement="bottom-start"
          disableHoverListener={!description}
        >
          <Typography
            variant="body2"
            color={description ? 'text.secondary' : 'text.disabled'}
            sx={{ ...clamp(3), minHeight: '4.8em', fontStyle: description ? 'normal' : 'italic' }}
          >
            {description || 'No description provided'}
          </Typography>
        </Tooltip>

        {/* Meta */}
        <Stack spacing={0.5} sx={{ mt: 'auto' }}>
          <MetaRow icon={OwnerIcon} label="Owner" value={pipeline.owner_username || '—'} />
          <MetaRow
            icon={ModifiedIcon}
            label="Last modified"
            value={formatDate(modifiedRaw) || 'N/A'}
            title={formatDateTime(modifiedRaw)}
          />
          <MetaRow
            icon={LastRunIcon}
            label="Last run"
            value={formatDate(pipeline.last_run_at) || 'Never'}
            title={formatDateTime(pipeline.last_run_at)}
          />
        </Stack>
      </CardContent>

      <Divider />

      <CardActions sx={{ px: 2, py: 1.25, gap: 0.5 }}>
        <Button
          size="small"
          startIcon={pipeline.can_edit ? <EditIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
          onClick={() => onOpen(pipeline)}
        >
          {pipeline.can_edit ? 'Edit' : 'View'}
        </Button>
        <Button
          size="small"
          color="inherit"
          startIcon={<DuplicateIcon fontSize="small" />}
          onClick={() => onDuplicate(pipeline)}
          sx={{ color: 'text.secondary', ml: '0 !important' }}
        >
          Duplicate
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={canRun ? '' : 'Only active pipelines can be run'}>
          <span>
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<RunIcon fontSize="small" />}
              disabled={!canRun}
              onClick={() => onRun(pipeline)}
            >
              Run
            </Button>
          </span>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

export default PipelineCard;
